import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowUpRight, Clock, Share2 } from "lucide-react";
import { NEWS as STATIC_NEWS } from "../data";
import { getNewsBySlug } from "../firebase";
import NewsCard from "../components/NewsCard";
import useNews from "../useNews";

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export default function NewsArticle() {
  const { slug } = useParams();
  const { list: allNews } = useNews();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        const fromDb = await getNewsBySlug(slug);
        if (!alive) return;
        if (fromDb) {
          setArticle(fromDb);
        } else {
          setArticle(STATIC_NEWS.find((n) => n.slug === slug) || null);
        }
      } catch {
        if (alive) setArticle(STATIC_NEWS.find((n) => n.slug === slug) || null);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [slug]);

  if (loading) {
    return <div className="max-w-md mx-auto px-6 py-32 text-center text-muted text-sm">Loading…</div>;
  }

  if (!article) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <p className="section-num"><strong>·</strong> 404</p>
        <h1 className="font-display text-display-md text-ink mt-5">
          Story not found.
        </h1>
        <Link to="/news" className="link-underline inline-flex items-center gap-2 mt-8 text-sm font-medium">
          <ArrowLeft className="h-4 w-4" /> Back to news
        </Link>
      </div>
    );
  }

  const related = allNews.filter((n) => n.slug !== slug).slice(0, 3);

  const share = () => {
    if (navigator.share) {
      navigator.share({ title: article.title, text: article.dek, url: window.location.href }).catch(() => {});
    } else {
      navigator.clipboard?.writeText(window.location.href);
      toast.success("Link copied");
    }
  };

  // Body may be an array (legacy) or a string (Firestore composer).
  const paragraphs = Array.isArray(article.body)
    ? article.body
    : (article.body || "").split(/\n\s*\n/).filter(Boolean);

  return (
    <article data-testid="news-article-page" className="relative">
      <section className="max-w-4xl mx-auto px-6 md:px-12 pt-10 md:pt-14 pb-12">
        <Link to="/news" className="link-underline inline-flex items-center gap-2 text-sm font-medium text-muted hover:text-coral">
          <ArrowLeft className="h-4 w-4" /> Back to news
        </Link>

        <p className="mt-10 inline-flex items-center px-3 py-1.5 rounded-full bg-sageMist text-sageDeep text-[10px] uppercase tracking-[0.22em] font-medium">
          {article.category}
        </p>
        <h1 className="font-display mt-5 text-display-lg text-ink tracking-tightest">
          {article.title}
        </h1>
        {article.dek && (
          <p className="mt-5 font-italic text-[22px] md:text-[26px] text-ink/75 leading-snug">
            {article.dek}
          </p>
        )}

        <div className="mt-8 flex items-center gap-5 text-[12px] uppercase tracking-[0.22em] text-muted">
          <span>{formatDate(article.date)}</span>
          {article.readTime && <span className="h-1 w-1 rounded-full bg-muted" />}
          {article.readTime && (
            <span className="inline-flex items-center gap-1.5">
              <Clock className="h-3 w-3" /> {article.readTime}
            </span>
          )}
          <button
            onClick={share}
            data-testid="article-share"
            className="ml-auto inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-bone border border-ink/10 hover:border-coral hover:text-coral text-ink text-[11px] transition-colors"
          >
            <Share2 className="h-3.5 w-3.5" /> Share
          </button>
        </div>
      </section>

      {article.cover && (
        <section className="max-w-6xl mx-auto px-6 md:px-12 pb-16">
          <div className="aspect-[16/9] rounded-4xl overflow-hidden bg-sageMist shadow-soft">
            <img
              src={article.cover}
              alt={article.title}
              className="w-full h-full object-cover"
            />
          </div>
        </section>
      )}

      <section className="max-w-3xl mx-auto px-6 md:px-12 pb-24">
        <div className="prose-like space-y-7 text-ink/80 text-[17px] leading-[1.65]">
          {paragraphs.map((p, i) => (
            <p key={i} className={i === 0 ? "first-letter:font-display first-letter:text-coral first-letter:text-[68px] first-letter:leading-[0.85] first-letter:float-left first-letter:mr-3 first-letter:mt-1" : ""}>
              {p}
            </p>
          ))}
        </div>

        <div className="mt-14 spore-divider text-ink" />

        <div className="mt-14 flex flex-wrap items-center justify-between gap-5">
          <Link to="/news" className="link-underline inline-flex items-center gap-2 text-sm font-medium">
            <ArrowLeft className="h-4 w-4" /> All stories
          </Link>
          <button
            onClick={share}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-ink text-bone text-sm hover:bg-coral transition-colors"
          >
            <Share2 className="h-4 w-4" /> Share this story
          </button>
        </div>
      </section>

      {related.length > 0 && (
        <section className="bg-boneSoft border-t border-ink/10 py-24 md:py-28">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex items-end justify-between gap-6">
              <h2 className="font-display text-display-md text-ink tracking-tightest">
                Keep reading,
                <br />
                <span className="font-italic text-coral">freshly picked.</span>
              </h2>
              <Link to="/news" className="link-underline inline-flex items-center gap-2 text-sm font-medium">
                All stories <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-10">
              {related.map((n) => (
                <NewsCard key={n.slug} item={n} />
              ))}
            </div>
          </div>
        </section>
      )}
    </article>
  );
}
